import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosClient from '../../../api/axiosClient';
import { paperSx, tableHeadCellSx, tableCellSx } from '../../../theme/tokens';
import { PageHeader, EmptyState } from '../../../components/ui';

/**
 * Customer Defaults — per-CUSTOMER auth_config + secret defaults
 * (config.customer_defaults on the SELECTED env).
 *
 * SUPER-ADMIN ONLY (server enforces requireRole('super-admin')) and editable
 * on ANY env: the env dropdown picks which env's store you are editing —
 * writes route browser-direct to that env, whose read-only fence exempts
 * this path by design (each env's Auth0 wiring/credentials are env-local).
 *
 * Consumed by:
 *  - publish: the customer's auth_config is applied to the tenant
 *    (customer store ALWAYS WINS) and secret_defaults keys are layered over
 *    the env row when TENANT_SECRET.{code} is created.
 *  - promote: the TARGET env's customer store supplies auth_config
 *    (auth_config itself is never promoted).
 *  - tenant creation: authConfig is seeded from the customer store,
 *    falling back to the ZIP payload when the customer has nothing.
 */

const AUTH0_FIELDS = [
  { key: 'domain', label: 'Auth0 domain', placeholder: 'myapp.us.auth0.com' },
  { key: 'issuer', label: 'Issuer URL', placeholder: 'https://myapp.us.auth0.com/' },
  { key: 'jwksUrl', label: 'JWKS URL', placeholder: 'https://myapp.us.auth0.com/.well-known/jwks.json' },
  { key: 'audience', label: 'Audience (JSON array)', placeholder: '["https://api.example.com"]' },
  { key: 'grantType', label: 'Grant type', placeholder: 'client_credentials' },
  { key: 'auth0ApiUrl', label: 'Auth0 API URL', placeholder: 'https://myapp.us.auth0.com/api/v2/' },
  { key: 'auth0TokenUrl', label: 'Token URL', placeholder: 'https://myapp.us.auth0.com/oauth/token' },
  { key: 'auth0UserInfoUrl', label: 'User info URL', placeholder: 'https://myapp.us.auth0.com/userinfo' },
  { key: 'auth0UserInfoByEmailUrl', label: 'User info by email URL', placeholder: 'https://myapp.us.auth0.com/api/v2/users-by-email' },
  { key: 'auth0VerificationEmailUrl', label: 'Verification email URL', placeholder: 'https://myapp.us.auth0.com/api/v2/jobs/verification-email' },
] as const;

type Auth0Key = typeof AUTH0_FIELDS[number]['key'];

/** Mirrors SECRET_DEFAULT_KEYS in server/src/lib/customerDefaults.ts. */
const SECRET_GROUPS: { label: string; keys: string[] }[] = [
  { label: 'Auth0', keys: ['auth0_client_id', 'auth0_client_secret', 'token_issuer', 'saml_check'] },
  { label: 'Encryption', keys: ['sym_encryption_key', 'customer_jwt_validation_key'] },
  { label: 'APL keys', keys: ['sunny_apl_public_key', 'sunny_apl_private_key', 'sunny_apl_private_key_pass_phrase', 'fis_apl_public_key'] },
  { label: 'FIS credentials', keys: ['fis_user_id', 'fis_pwd', 'fis_source_id'] },
  { label: 'Redemption', keys: ['redemption_vendor_key_0', 'redemption_vendor_key_1'] },
];

interface CustomerRow {
  customer_code: string;
  customer_name: string | null;
  tenant_count: number;
  tenants: string[];
  auth_config: Record<string, unknown> | null;
  secret_defaults: Record<string, string> | null;
  updated_at: string | null;
  updated_by: string | null;
}

function extractAuth0Fields(authConfig: Record<string, unknown> | null): Record<string, string> {
  const auth0 = authConfig?.auth0;
  if (!auth0 || typeof auth0 !== 'object' || Array.isArray(auth0)) return {};
  const out: Record<string, string> = {};
  for (const { key } of AUTH0_FIELDS) {
    const val = (auth0 as Record<string, unknown>)[key];
    if (val == null) continue;
    out[key] = Array.isArray(val) ? JSON.stringify(val) : String(val);
  }
  return out;
}

function extractExtraKeys(authConfig: Record<string, unknown> | null): string {
  if (!authConfig) return '';
  const extra: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(authConfig)) {
    if (k !== 'auth0') extra[k] = v;
  }
  return Object.keys(extra).length > 0 ? JSON.stringify(extra, null, 2) : '';
}

function buildAuthConfig(
  auth0Fields: Record<string, string>,
  extraJson: string,
): { config: Record<string, unknown> | null; error: string | null } {
  const auth0: Record<string, unknown> = {};
  for (const { key } of AUTH0_FIELDS) {
    const val = auth0Fields[key]?.trim();
    if (!val) continue;
    if (key === 'audience') {
      try {
        const parsed = JSON.parse(val);
        if (!Array.isArray(parsed)) {
          return { config: null, error: 'Audience must be a JSON array, e.g. ["https://api.example.com"]' };
        }
        auth0[key] = parsed;
      } catch {
        return { config: null, error: 'Audience: invalid JSON — must be an array like ["https://api.example.com"]' };
      }
    } else {
      auth0[key] = val;
    }
  }

  let extra: Record<string, unknown> = {};
  if (extraJson.trim()) {
    try {
      const parsed = JSON.parse(extraJson);
      if (typeof parsed !== 'object' || Array.isArray(parsed) || parsed == null) {
        return { config: null, error: 'Additional keys must be a JSON object' };
      }
      extra = parsed as Record<string, unknown>;
    } catch (e: any) {
      return { config: null, error: `Additional keys — invalid JSON: ${e?.message ?? e}` };
    }
  }

  const hasAuth0 = Object.keys(auth0).length > 0;
  const hasExtra = Object.keys(extra).length > 0;
  if (!hasAuth0 && !hasExtra) return { config: null, error: null };
  return { config: { ...(hasAuth0 ? { auth0 } : {}), ...extra }, error: null };
}

const CustomerDefaultsAdminPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<CustomerRow | null>(null);
  const [auth0Fields, setAuth0Fields] = useState<Record<string, string>>({});
  const [authError, setAuthError] = useState<string | null>(null);
  const [extraJson, setExtraJson] = useState('');
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const [showSecrets, setShowSecrets] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const { data, isLoading, error, refetch, isFetching } = useQuery<CustomerRow[]>({
    queryKey: ['customer-defaults'],
    queryFn: async () => (await axiosClient.get('/api/customer-defaults')).data,
  });

  const saveMutation = useMutation({
    mutationFn: async (payload: { customerCode: string; auth_config: unknown; secret_defaults: Record<string, string> | null }) =>
      (await axiosClient.put(`/api/customer-defaults/${encodeURIComponent(payload.customerCode)}`, payload)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-defaults'] });
      setEditing(null);
      setSaveError(null);
    },
    onError: (e: any) => {
      setSaveError(e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Save failed');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (customerCode: string) =>
      (await axiosClient.delete(`/api/customer-defaults/${encodeURIComponent(customerCode)}`)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customer-defaults'] }),
  });

  const openEdit = (row: CustomerRow) => {
    setEditing(row);
    setAuth0Fields(extractAuth0Fields(row.auth_config));
    setExtraJson(extractExtraKeys(row.auth_config));
    setAuthError(null);
    setSaveError(null);
    setShowSecrets(false);
    const allKeys = SECRET_GROUPS.flatMap((g) => g.keys);
    const s: Record<string, string> = {};
    for (const k of allKeys) s[k] = row.secret_defaults?.[k] ?? '';
    setSecrets(s);
  };

  const onSave = () => {
    const { config, error: buildError } = buildAuthConfig(auth0Fields, extraJson);
    if (buildError) {
      setAuthError(buildError);
      return;
    }
    setAuthError(null);
    const sd: Record<string, string> = {};
    for (const [k, v] of Object.entries(secrets)) {
      if (v && v.trim() !== '') sd[k] = v;
    }
    saveMutation.mutate({
      customerCode: editing!.customer_code,
      auth_config: config,
      secret_defaults: Object.keys(sd).length > 0 ? sd : null,
    });
  };

  const secretKeyCount = (row: CustomerRow): number =>
    row.secret_defaults ? Object.keys(row.secret_defaults).length : 0;

  const setAuth0Field = (key: Auth0Key, value: string) =>
    setAuth0Fields((prev) => ({ ...prev, [key]: value }));

  return (
    <Box>
      <PageHeader
        title="Customer Defaults"
        subtitle="Per-customer auth config + secret defaults for the SELECTED environment. Publish applies auth_config to the customer's tenants (customer store always wins) and layers secret keys over the env defaults when tenant secrets are created. Super-admin only."
        actions={
          <Button startIcon={isFetching ? <CircularProgress size={16} /> : <RefreshIcon />} onClick={() => refetch()} disabled={isFetching}>
            Refresh
          </Button>
        }
      />

      {error != null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as any)?.response?.status === 403
            ? 'Super-admin role required to view customer defaults.'
            : `Failed to load: ${(error as any)?.message ?? String(error)}`}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : !data || data.length === 0 ? (
        <EmptyState title="No customers found" description="No customers exist on this environment yet." />
      ) : (
        <TableContainer component={Paper} sx={paperSx}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeadCellSx}>Customer</TableCell>
                <TableCell sx={tableHeadCellSx}>Code</TableCell>
                <TableCell sx={tableHeadCellSx} align="center">Tenants</TableCell>
                <TableCell sx={tableHeadCellSx} align="center">Auth Config</TableCell>
                <TableCell sx={tableHeadCellSx} align="center">Secret Keys</TableCell>
                <TableCell sx={tableHeadCellSx}>Updated</TableCell>
                <TableCell sx={tableHeadCellSx} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row) => (
                <TableRow key={row.customer_code} hover>
                  <TableCell sx={tableCellSx}>{row.customer_name || '—'}</TableCell>
                  <TableCell sx={{ ...tableCellSx, fontFamily: 'monospace', fontSize: 12 }}>{row.customer_code}</TableCell>
                  <TableCell sx={tableCellSx} align="center">
                    <Tooltip title={(row.tenants ?? []).join(', ') || 'No config tenants'}>
                      <span>{row.tenant_count}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={tableCellSx} align="center">
                    {row.auth_config && Object.keys(row.auth_config).length > 0
                      ? <Chip label="configured" color="success" size="small" />
                      : <Chip label="none" size="small" variant="outlined" />}
                  </TableCell>
                  <TableCell sx={tableCellSx} align="center">
                    {secretKeyCount(row) > 0
                      ? <Chip label={`${secretKeyCount(row)} key${secretKeyCount(row) === 1 ? '' : 's'}`} color="info" size="small" />
                      : <Chip label="env defaults" size="small" variant="outlined" />}
                  </TableCell>
                  <TableCell sx={tableCellSx}>
                    {row.updated_at ? `${new Date(row.updated_at).toLocaleString()} · ${row.updated_by ?? ''}` : '—'}
                  </TableCell>
                  <TableCell sx={tableCellSx} align="right">
                    <Tooltip title="Edit defaults">
                      <IconButton size="small" onClick={() => openEdit(row)} aria-label="Edit customer defaults">
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    {(row.auth_config || secretKeyCount(row) > 0) && (
                      <Tooltip title="Remove all defaults for this customer (tenants fall back to env defaults / ZIP)">
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (window.confirm(`Remove ALL customer defaults for ${row.customer_code}?`)) {
                              deleteMutation.mutate(row.customer_code);
                            }
                          }}
                          aria-label="Delete customer defaults"
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Customer Defaults — {editing?.customer_name || editing?.customer_code}
          <Typography variant="body2" color="text.secondary">
            {editing?.customer_code} · applies to {editing?.tenant_count ?? 0} tenant(s) on this environment
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          {authError && <Alert severity="error" sx={{ mb: 2 }}>{authError}</Alert>}

          {/* Auth Config — structured fields */}
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Auth0 config</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Publish applies these to every tenant of this customer (customer store always wins).
            Leave all fields empty to not manage auth config for this customer.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            {AUTH0_FIELDS.map(({ key, label, placeholder }) => (
              <TextField
                key={key}
                label={label}
                value={auth0Fields[key] ?? ''}
                onChange={(e) => setAuth0Field(key, e.target.value)}
                size="small"
                fullWidth
                placeholder={placeholder}
                InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
              />
            ))}
          </Box>

          {/* Additional / advanced keys */}
          <Accordion disableGutters elevation={0} sx={{ mt: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
              <Typography variant="caption" color="text.secondary">
                Additional config keys (raw JSON) — use for keys not listed above
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <TextField
                value={extraJson}
                onChange={(e) => setExtraJson(e.target.value)}
                multiline
                minRows={4}
                maxRows={10}
                fullWidth
                size="small"
                placeholder={'{ "someOtherKey": "value" }'}
                InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
              />
            </AccordionDetails>
          </Accordion>

          <Divider sx={{ my: 2.5 }} />

          {/* Secret Defaults — grouped, masked */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="subtitle2">Secret defaults (per-key overrides)</Typography>
            <Tooltip title={showSecrets ? 'Hide values' : 'Show values'}>
              <IconButton size="small" onClick={() => setShowSecrets((v) => !v)} aria-label={showSecrets ? 'Hide secret values' : 'Show secret values'}>
                {showSecrets ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Only set keys that differ for this customer — empty fields fall through to env defaults.
            Masked values (****…) round-trip unchanged; clearing a field removes the override.
          </Typography>

          {SECRET_GROUPS.map((group, gi) => (
            <Box key={group.label} sx={{ mb: gi < SECRET_GROUPS.length - 1 ? 2.5 : 0 }}>
              <Typography variant="overline" color="text.secondary" sx={{ fontSize: 10, letterSpacing: 1 }}>
                {group.label}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mt: 0.5 }}>
                {group.keys.map((k) => (
                  <TextField
                    key={k}
                    label={k}
                    value={secrets[k] ?? ''}
                    onChange={(e) => setSecrets((s) => ({ ...s, [k]: e.target.value }))}
                    size="small"
                    fullWidth
                    type={showSecrets ? 'text' : 'password'}
                    autoComplete="new-password"
                    InputProps={{
                      sx: { fontFamily: 'monospace', fontSize: 12 },
                      endAdornment: secrets[k] ? (
                        <InputAdornment position="end">
                          <Chip
                            label="set"
                            size="small"
                            color="info"
                            sx={{ height: 18, fontSize: 10 }}
                          />
                        </InputAdornment>
                      ) : undefined,
                    }}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" onClick={onSave} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerDefaultsAdminPage;
